/**
 * The console's half of "Forgot your password?" (owner request 2026-10-12): `/forgot-password` posts the address to
 * `requestPasswordReset` (`POST /v1/auth/password-reset`) and `/reset-password#<token>` posts the token and the new
 * password to `confirmPasswordReset` (`POST /v1/auth/password-reset/confirm`), apps/api/src/routes/password-reset.
 *
 * OPTIONAL ON THE GATEWAY, like the emailed-link pages' methods (`../mail-landing/mail-landing-gateway.ts`): merged onto
 * `ConsoleGateway` here, the API binding spread into `apiGateway` and the sandbox binding into `sandboxGateway`. A
 * gateway without them draws the pages' "not available here" state rather than a form that cannot work.
 *
 * THE SANDBOX sends no email, so it never has a real link: it accepts every address (the same answer the API gives),
 * and takes any well-formed token once — so the whole flow can be walked with `pnpm dev:sandbox` by opening
 * `/reset-password#sandbox-reset-link-0000` — refusing a short password, a reused link and a malformed one with the
 * API's own codes and sentences.
 */
import { ApiError, assertOk, type WeTalkApiClient } from "@wetalk/api-client";

declare module "../../api/gateway.js" {
  interface ConsoleGateway {
    /** Asks for a reset link. Resolves the same way for every address; `404 not_found` while no email can be sent, `429 rate_limited`. */
    readonly requestPasswordReset?: (emailAddress: string) => Promise<void>;
    /**
     * Sets a new password from the link's token. `404 not_found` for a used, expired or unknown link; `400
     * password_too_short` / `validation_failed` for a password the policy refuses (the link stays usable); `429`.
     */
    readonly confirmPasswordReset?: (token: string, newPassword: string) => Promise<void>;
  }
}

/** The API's own words, restated so the sandbox's refusals read (and translate) exactly as the API's do. */
const LINK_UNUSABLE = "That reset link has been used or has expired. Ask for a new one.";
const TOO_SHORT = "Passwords are at least 10 characters.";
const PASSWORD_MINIMUM = 10;
const TOKEN_SHAPE = /^[A-Za-z0-9_-]{16,256}$/;

/** `data.state` of a 2xx answer; a refusal raises the API's `ApiError` (`assertOk`). */
function stateOf(answered: Parameters<typeof assertOk>[0]): unknown {
  const body = assertOk(answered);
  const data = typeof body === "object" && body !== null ? (body as Readonly<Record<string, unknown>>)["data"] : undefined;
  return typeof data === "object" && data !== null ? (data as Readonly<Record<string, unknown>>)["state"] : undefined;
}

/** The API binding, spread into `apiGateway` beside the other slices. */
export function passwordResetApiSlice(client: WeTalkApiClient): {
  readonly requestPasswordReset: (emailAddress: string) => Promise<void>;
  readonly confirmPasswordReset: (token: string, newPassword: string) => Promise<void>;
} {
  return {
    async requestPasswordReset(emailAddress) {
      if (stateOf(await client.requestPasswordReset({ body: { email: emailAddress } })) !== "accepted") {
        throw new ApiError(500, { error: { code: "internal_error", message: "requestPasswordReset answered an unknown state" } }, null);
      }
    },
    async confirmPasswordReset(token, newPassword) {
      if (stateOf(await client.confirmPasswordReset({ body: { token, new_password: newPassword } })) !== "reset") {
        throw new ApiError(500, { error: { code: "internal_error", message: "confirmPasswordReset answered an unknown state" } }, null);
      }
    },
  };
}

/** The sandbox binding, spread into `sandboxGateway`. */
export function passwordResetSandboxSlice(): {
  readonly requestPasswordReset: (emailAddress: string) => Promise<void>;
  readonly confirmPasswordReset: (token: string, newPassword: string) => Promise<void>;
} {
  const spent = new Set<string>();
  return {
    async requestPasswordReset() {
      // The same answer for every address, as the API gives; the sandbox sends no email.
    },
    async confirmPasswordReset(token, newPassword) {
      if (newPassword.length < PASSWORD_MINIMUM) {
        throw new ApiError(400, { error: { code: "password_too_short", message: TOO_SHORT, field: "new_password" } }, null);
      }
      if (!TOKEN_SHAPE.test(token) || spent.has(token)) throw new ApiError(404, { error: { code: "not_found", message: LINK_UNUSABLE } }, null);
      spent.add(token);
    },
  };
}
